import { useEffect, useMemo, useState } from "react";

import { apiClient } from "../../api/client";
import { fetchRunTablePaginated, type RunTableData } from "../../api/runTable";
import { localFixtureForProject } from "../../api/projects";
import type { SavedPlot } from "../../api/plots";
import type { BranchesResponse, GraphResponse, ScenarioResponse } from "../../pages/v2-workbench/types";
import type { V2TableData } from "../v2-workspace/types";
import { buildV3Data } from "./adapters";
import { loadV3Fixture, normalizeV3FixtureLevel, type V3FixtureLevel } from "./fixtureData";
import type { ProjectSummary, V3WorkbenchData } from "./types";

const DEFAULT_BRANCH = "main";
const LIVE_PROJECTS_TIMEOUT_MS = 10_000;
const LIVE_BRANCHES_TIMEOUT_MS = 10_000;
const LIVE_SCENARIO_TIMEOUT_MS = 20_000;
const LIVE_GRAPH_TIMEOUT_MS = 15_000;
const LIVE_PLOTS_TIMEOUT_MS = 15_000;
const UUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type LoadState =
  | { status: "loading"; data: V3WorkbenchData }
  | { status: "ready"; data: V3WorkbenchData }
  | { status: "error"; data: V3WorkbenchData; error: string };

async function fetchJson<T>(path: string, signal?: AbortSignal, timeoutMs = 2800): Promise<T> {
  const requestController = new AbortController();
  const abort = () => requestController.abort(signal?.reason);
  const timeout = window.setTimeout(() => requestController.abort(`Timed out after ${timeoutMs}ms`), timeoutMs);
  signal?.addEventListener("abort", abort, { once: true });
  try {
    const apiPath = path.startsWith("/api/") ? path.slice(4) : path;
    return await apiClient<T>(apiPath, { signal: requestController.signal });
  } finally {
    window.clearTimeout(timeout);
    signal?.removeEventListener("abort", abort);
  }
}

export function isLiveThreadProjectId(projectId: string | null | undefined): boolean {
  return Boolean(projectId && UUID_LIKE.test(projectId));
}

function firstProjectId(projects: readonly ProjectSummary[]): ProjectSummary | null {
  return projects.find((project) => isLiveThreadProjectId(project.id)) ?? null;
}

function selectBranch(branches: BranchesResponse | null, requested: string | null): string {
  if (requested && branches?.branches.some((branch) => branch.name === requested)) return requested;
  return (
    branches?.branches.find((branch) => branch.name === "avl-cfd-lineage")?.name ??
    // A POPULATED main is the default before any other branch (FXC-9009 live
    // bug: an archive branch — non-main with operations, e.g. legacy-doe —
    // hijacked every ?branch-less load the moment it was created, flipping the
    // whole workbench off main). The non-main clause below only exists to skip
    // an EMPTY main.
    branches?.branches.find((branch) => branch.name === "main" && branch.operation_count > 0)?.name ??
    branches?.branches.find((branch) => branch.name !== "main" && branch.operation_count > 0)?.name ??
    branches?.branches[0]?.name ??
    requested ??
    DEFAULT_BRANCH
  );
}

function searchParams(searchKey?: string): URLSearchParams {
  if (searchKey) return new URLSearchParams(searchKey);
  if (typeof window === "undefined") return new URLSearchParams();
  return new URLSearchParams(window.location.search);
}

function requestedFixtureLevel(searchKey?: string): V3FixtureLevel | null {
  return normalizeV3FixtureLevel(searchParams(searchKey).get("fixture"));
}

function shouldUseRunTableFallback(searchKey?: string): boolean {
  const value = searchParams(searchKey).get("runTable");
  return value === "1" || value === "true";
}

type V3LiveSourcePreference = "auto" | "scenario" | "run-table";

function requestedLiveSource(searchKey?: string): V3LiveSourcePreference {
  const params = searchParams(searchKey);
  const value = (params.get("source") ?? params.get("dataSource") ?? "").toLowerCase();
  if (value === "run-table" || value === "runtable" || value === "flow360") return "run-table";
  if (value === "scenario") return "scenario";
  if (shouldUseRunTableFallback(searchKey)) return "run-table";
  return "auto";
}

function requestedRunTableLimit(sourcePreference: V3LiveSourcePreference, searchKey?: string): number | undefined {
  const params = searchParams(searchKey);
  const value = params.get("runTableLimit") ?? params.get("rowLimit") ?? params.get("limit");
  const parsed = value ? Number(value) : sourcePreference === "run-table" ? 10_000 : 5_000;
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return Math.max(1, Math.min(10_000, Math.floor(parsed)));
}

function requestedProjectionRowBudget(searchKey?: string): number | undefined {
  const params = searchParams(searchKey);
  const value = params.get("visibleRows") ?? params.get("projectionRows") ?? params.get("rowBudget");
  if (!value) return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return Math.max(1, Math.min(10_000, Math.floor(parsed)));
}

function shouldFetchLiveGraph(searchKey?: string): boolean {
  const params = searchParams(searchKey);
  const value = params.get("graph") ?? params.get("fullGraph");
  return value === "1" || value === "true";
}

function hasExplicitProject(routeProjectId?: string, searchKey?: string): boolean {
  return Boolean(routeProjectId || searchParams(searchKey).get("project"));
}

function fixtureLevelForLocalProject(projectId: string | null | undefined, branch: string | null | undefined): V3FixtureLevel | null {
  const normalizedProject = projectId?.toLowerCase() ?? "";
  const normalizedBranch = branch?.toLowerCase() ?? "";
  if (normalizedProject.includes("tsangpo") || normalizedBranch === "tsangpo-fixture") return "tsangpo";
  return null;
}

function localProjectId(routeProjectId?: string, searchKey?: string): string | null {
  const projectId = routeProjectId ?? searchParams(searchKey).get("project");
  return localFixtureForProject(projectId) ? projectId ?? null : null;
}

function withLocalProjectIdentity(data: V3WorkbenchData, projectId: string | null): V3WorkbenchData {
  if (!projectId) return data;
  return {
    ...data,
    project: {
      id: projectId,
      name: data.project?.name ?? "Local Thread project",
      description: data.project?.description,
      metadata: {
        ...(data.project?.metadata ?? {}),
        local_only: true,
        fixture: localFixtureForProject(projectId),
      },
    },
  };
}

async function loadLocalV2Project(
  projectId: string | null | undefined,
  branch: string,
  rowBudget: number | undefined,
  signal?: AbortSignal,
): Promise<V3WorkbenchData | null> {
  const normalized = projectId?.toLowerCase();
  let module: { default: unknown } | null = null;
  if (normalized === "x57_maxwell" || normalized === "x57") {
    module = await import("../v2-workspace/mock-data/x57_maxwell.json");
  } else if (normalized === "cessna_182" || normalized === "cessna") {
    module = await import("../v2-workspace/mock-data/cessna_182.json");
  }
  if (!module || signal?.aborted) return null;

  const local = module.default as V2TableData;
  const runTable: RunTableData = {
    columns: local.columns.map((column) => ({
      field: column.field,
      headerName: column.headerName,
    })),
    rows: local.rows as Record<string, unknown>[],
    total: local.rows.length,
  };

  return buildV3Data({
    project: {
      id: local.project,
      name: local.project_label ?? local.project,
    },
    branch: branch || "local-bundled",
    scenario: null,
    graph: null,
    runTable,
    rowBudget: rowBudget ?? local.rows.length,
    errors: [],
    allowMockFallback: false,
  });
}

export function useV3Data(routeProjectId?: string, requestedBranch?: string | null, searchKey?: string): LoadState {
  const localId = localProjectId(routeProjectId, searchKey);
  const fixtureLevel = requestedFixtureLevel(searchKey) ?? localFixtureForProject(localId);
  const liveSourcePreference = requestedLiveSource(searchKey);
  const runTableLimitPreference = requestedRunTableLimit(liveSourcePreference, searchKey);
  const projectionRowBudget = requestedProjectionRowBudget(searchKey);
  const fetchLiveGraphPreference = shouldFetchLiveGraph(searchKey);
  const explicitProject = hasExplicitProject(routeProjectId, searchKey);
  const [state, setState] = useState<LoadState>(() => ({
    status: "loading",
    data: buildV3Data({
      project: routeProjectId ? { id: routeProjectId, name: "Thread project" } : null,
      branch: requestedBranch ?? DEFAULT_BRANCH,
      scenario: null,
      graph: null,
      runTable: null,
      rowBudget: projectionRowBudget,
      allowMockFallback: false,
    }),
  }));

  useEffect(() => {
    const controller = new AbortController();
    const commit = (next: LoadState) => {
      if (!controller.signal.aborted) setState(next);
    };
    const commitFromCurrent = (next: (current: LoadState) => LoadState) => {
      if (!controller.signal.aborted) setState(next);
    };

    async function load() {
      commitFromCurrent((current) => ({ status: "loading", data: current.data }));
      const errors: string[] = [];
      const sourcePreference = liveSourcePreference;
      const runTableLimit = runTableLimitPreference;
      const fetchLiveGraph = fetchLiveGraphPreference;
      const requestedProjectId = routeProjectId ?? searchParams(searchKey).get("project");
      let project: ProjectSummary | null = isLiveThreadProjectId(requestedProjectId) ? { id: requestedProjectId!, name: "Thread project" } : null;

      if (fixtureLevel) {
        try {
          const fixtureData = await loadV3Fixture(fixtureLevel, controller.signal);
          commit({ status: "ready", data: withLocalProjectIdentity(fixtureData, localId) });
        } catch (error) {
          const message = `fixture ${fixtureLevel}: ${error instanceof Error ? error.message : String(error)}`;
          commitFromCurrent((current) => ({ status: "error", data: { ...current.data, errors: [...current.data.errors, message] }, error: message }));
        }
        return;
      }

      const localProjectId = routeProjectId ?? searchParams(searchKey).get("project");
      const localData = await loadLocalV2Project(
        localProjectId,
        requestedBranch ?? "local-bundled",
        projectionRowBudget,
        controller.signal,
      );
      if (localData?.rows.length) {
        commit({ status: "ready", data: localData });
        return;
      }

      const localFixtureLevel = fixtureLevelForLocalProject(requestedProjectId, requestedBranch);
      if (requestedProjectId && !isLiveThreadProjectId(requestedProjectId)) {
        if (localFixtureLevel) {
          try {
            commit({ status: "ready", data: await loadV3Fixture(localFixtureLevel, controller.signal) });
          } catch (error) {
            const message = `fixture ${localFixtureLevel}: ${error instanceof Error ? error.message : String(error)}`;
            commitFromCurrent((current) => ({ status: "error", data: { ...current.data, errors: [...current.data.errors, message] }, error: message }));
          }
          return;
        }

        commit({
          status: "ready",
          data: buildV3Data({
            project: { id: requestedProjectId, name: "Thread project" },
            branch: requestedBranch ?? DEFAULT_BRANCH,
            scenario: null,
            graph: null,
            runTable: null,
            rowBudget: projectionRowBudget,
            errors: [`Project "${requestedProjectId}" is not a live Thread project id. Live V3 project APIs require a UUID.`],
            allowMockFallback: false,
          }),
        });
        return;
      }

      if (!project) {
        try {
          project = firstProjectId(await fetchJson<ProjectSummary[]>("/api/projects", controller.signal, LIVE_PROJECTS_TIMEOUT_MS));
        } catch (error) {
          errors.push(`projects: ${error instanceof Error ? error.message : String(error)}`);
        }
      } else {
        try {
          const projects = await fetchJson<ProjectSummary[]>("/api/projects", controller.signal, LIVE_PROJECTS_TIMEOUT_MS);
          project = projects.find((item) => item.id === project?.id && isLiveThreadProjectId(item.id)) ?? project;
        } catch (error) {
          errors.push(`projects: ${error instanceof Error ? error.message : String(error)}`);
        }
      }

      if (!project) {
        try {
          const data = await loadV3Fixture("low", controller.signal, [...errors, "Live API unavailable; showing low-detail provenance fixture."]);
          commit({ status: "ready", data });
        } catch (error) {
          const data = buildV3Data({
            project: null,
            branch: requestedBranch ?? DEFAULT_BRANCH,
            scenario: null,
            graph: null,
            runTable: null,
            rowBudget: projectionRowBudget,
            errors,
          });
          commit({ status: "error", data, error: `No Thread project is available. Fixture fallback failed: ${error instanceof Error ? error.message : String(error)}` });
        }
        return;
      }

      let branches: BranchesResponse | null = null;
      try {
        branches = await fetchJson<BranchesResponse>(
          `/api/v2/branches?project_id=${encodeURIComponent(project.id)}`,
          controller.signal,
          LIVE_BRANCHES_TIMEOUT_MS,
        );
      } catch (error) {
        errors.push(`branches: ${error instanceof Error ? error.message : String(error)}`);
      }

      const branch = selectBranch(branches, requestedBranch ?? null);
      const params = new URLSearchParams({
        project_id: project.id,
        branch,
        include_inherited: "false",
      });

      let runTableResult: RunTableData | null | undefined;
      const loadRunTable = async (): Promise<RunTableData | null> => {
        if (runTableResult !== undefined) return runTableResult;
        try {
          runTableResult = await fetchRunTablePaginated({ projectId: project!.id, branch, limit: runTableLimit });
        } catch (error) {
          errors.push(`run-table: ${error instanceof Error ? error.message : String(error)}`);
          runTableResult = null;
        }
        return runTableResult;
      };

      let savedPlotsResult: SavedPlot[] | undefined;
      const loadSavedPlots = async (): Promise<SavedPlot[]> => {
        if (savedPlotsResult !== undefined) return savedPlotsResult;
        const plotParams = new URLSearchParams({
          project_id: project!.id,
          branch,
          include_collection_members: "true",
          limit: "200",
        });
        try {
          savedPlotsResult = await fetchJson<SavedPlot[]>(`/api/plots?${plotParams.toString()}`, controller.signal, LIVE_PLOTS_TIMEOUT_MS);
        } catch (error) {
          errors.push(`plots: ${error instanceof Error ? error.message : String(error)}`);
          savedPlotsResult = [];
        }
        return savedPlotsResult;
      };

      let graph: GraphResponse | null = null;
      let graphLoaded = false;
      const loadGraph = async (): Promise<GraphResponse | null> => {
        if (graphLoaded) return graph;
        graphLoaded = true;
        try {
          graph = await fetchJson<GraphResponse>(`/api/v2/graph?${params.toString()}`, controller.signal, LIVE_GRAPH_TIMEOUT_MS);
        } catch (error) {
          errors.push(`graph: ${error instanceof Error ? error.message : String(error)}`);
        }
        return graph;
      };

      if (sourcePreference === "run-table") {
        const [runTable, savedPlots, runTableGraph] = await Promise.all([
          loadRunTable(),
          loadSavedPlots(),
          fetchLiveGraph ? loadGraph() : Promise.resolve(null),
        ]);
        const runTableData = buildV3Data({
          project,
          branch,
          scenario: null,
          graph: runTableGraph,
          runTable,
          savedPlots,
          rowBudget: projectionRowBudget,
          errors,
          allowMockFallback: false,
        });
        if (runTableData.source === "run-table" && runTableData.rows.length) {
          commit({ status: "ready", data: runTableData });
          return;
        }
      }

      const scenarioResult = sourcePreference === "run-table"
        ? { status: "rejected" as const, reason: "run-table source requested" }
        : await Promise.resolve(fetchJson<ScenarioResponse>(
          `/api/v2/review/scenario?${params.toString()}`,
          controller.signal,
          LIVE_SCENARIO_TIMEOUT_MS,
        ))
          .then((value) => ({ status: "fulfilled" as const, value }))
          .catch((reason: unknown) => ({ status: "rejected" as const, reason }));

      const scenario = scenarioResult.status === "fulfilled" ? scenarioResult.value : null;

      if (scenarioResult.status === "rejected" && sourcePreference !== "run-table") errors.push(`scenario: ${String(scenarioResult.reason)}`);
      const [liveGraph, savedPlots] = await Promise.all([
        fetchLiveGraph ? loadGraph() : Promise.resolve(null),
        loadSavedPlots(),
      ]);
      graph = liveGraph;

      const scenarioData = buildV3Data({ project, branch, scenario, graph, runTable: null, savedPlots, rowBudget: projectionRowBudget, errors, allowMockFallback: false });
      if (scenarioData.source === "scenario" && scenarioData.rows.length) {
        commit({ status: "ready", data: scenarioData });
        return;
      }

      const runTable: RunTableData | null = sourcePreference === "scenario" ? null : await loadRunTable();

      const data = buildV3Data({ project, branch, scenario, graph, runTable, savedPlots, rowBudget: projectionRowBudget, errors, allowMockFallback: false });
      if (data.rows.length) {
        if (data.source === "mock-fallback") {
          commit({
            status: "ready",
            data: await loadV3Fixture("low", controller.signal, [...errors, "Live graph has no projection table; showing low-detail provenance fixture. Add ?runTable=1 to try /api/run-table."]),
          });
        } else {
          commit({ status: "ready", data });
        }
      } else if (explicitProject) {
        commit({ status: "ready", data });
      } else {
        try {
          commit({
            status: "ready",
            data: await loadV3Fixture("low", controller.signal, [...errors, "Live data returned no rows; showing low-detail provenance fixture. Add ?runTable=1 to try /api/run-table."]),
          });
        } catch {
          commit({ status: "error", data, error: "No rows are available." });
        }
      }
    }

    void load().catch((error: unknown) => {
      if (!controller.signal.aborted) {
        const message = error instanceof Error ? error.message : String(error);
        commitFromCurrent((current) => ({ status: "error", data: { ...current.data, errors: [...current.data.errors, message] }, error: message }));
      }
    });

    return () => controller.abort();
  }, [explicitProject, fetchLiveGraphPreference, fixtureLevel, liveSourcePreference, localId, projectionRowBudget, requestedBranch, routeProjectId, runTableLimitPreference]);

  return useMemo(() => state, [state]);
}
